/**
 * Card de status de conexão com a Meta Graph API
 * Permite verificar conexão em tempo real
 * 
 * @version 1.0.0
 * @date 2026-01-16
 */

import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Wifi, 
  WifiOff, 
  RefreshCw,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Loader2,
  Clock
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';
import type { ConnectionValidationResult } from '@/hooks/useInboxDetails';

// ============================================================================
// INTERFACES
// ============================================================================

interface ConnectionStatusCardProps {
  onCheckConnection: () => Promise<ConnectionValidationResult>;
  onReconnect?: () => void;
  hasCredentials: boolean;
}

// ============================================================================
// COMPONENTE
// ============================================================================

export function ConnectionStatusCard({ 
  onCheckConnection,
  onReconnect,
  hasCredentials
}: ConnectionStatusCardProps) {
  const { t } = useTranslation('pages');
  
  // Estados locais
  const [isChecking, setIsChecking] = useState(false);
  const [lastCheck, setLastCheck] = useState<Date | null>(null);
  const [result, setResult] = useState<ConnectionValidationResult | null>(null);

  // Handler para verificar conexão
  const handleCheckConnection = async () => {
    setIsChecking(true);
    try {
      const validationResult = await onCheckConnection();
      setResult(validationResult);
      setLastCheck(new Date());
    } finally {
      setIsChecking(false);
    }
  };

  // Determinar ícone e cor baseado no status
  const getStatusConfig = () => {
    if (!result) {
      return {
        icon: AlertCircle,
        color: 'text-muted-foreground',
        bgColor: 'bg-muted',
        label: t('inboxDetails.notChecked', 'Não verificado'),
      };
    }

    switch (result.status) {
      case 'connected':
        return {
          icon: CheckCircle2,
          color: 'text-green-600 dark:text-green-400',
          bgColor: 'bg-green-500/10',
          label: t('inboxDetails.connectionActive', 'Conexão ativa'),
        };
      case 'disconnected':
        return {
          icon: WifiOff,
          color: 'text-orange-600 dark:text-orange-400',
          bgColor: 'bg-orange-500/10',
          label: t('inboxDetails.connectionInactive', 'Desconectado'),
        };
      case 'no_credentials':
        return {
          icon: AlertCircle,
          color: 'text-amber-600 dark:text-amber-400',
          bgColor: 'bg-amber-500/10',
          label: t('inboxDetails.noCredentials', 'Sem credenciais'),
        };
      case 'error':
      default:
        return {
          icon: XCircle,
          color: 'text-destructive',
          bgColor: 'bg-destructive/10',
          label: t('inboxDetails.connectionError', 'Erro de conexão'),
        };
    }
  };

  const statusConfig = getStatusConfig();
  const StatusIcon = statusConfig.icon;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Wifi className="h-4 w-4 text-primary" />
          {t('inboxDetails.connectionStatus', 'Status da Conexão')}
        </CardTitle>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Status atual */}
        <div className={cn(
          "flex items-center justify-between p-4 rounded-lg",
          statusConfig.bgColor
        )}>
          <div className="flex items-center gap-3">
            <StatusIcon className={cn("h-5 w-5", statusConfig.color)} />
            <div>
              <p className={cn("font-medium", statusConfig.color)}>
                {statusConfig.label}
              </p>
              {result?.message && (
                <p className="text-sm text-muted-foreground">
                  {result.message}
                </p>
              )}
            </div>
          </div>
          
          {result?.status === 'connected' && (
            <Badge className="bg-green-500/10 text-green-600 border-green-500/20">
              <CheckCircle2 className="h-3 w-3 mr-1" />
              Online
            </Badge>
          )}
        </div>

        {/* Detalhes da verificação (se conectado) */}
        {result?.status === 'connected' && result.details && (
          <div className="space-y-2 p-3 bg-muted/50 rounded-lg">
            {result.details.verified_name && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Nome Verificado:</span>
                <span className="font-medium">{result.details.verified_name}</span>
              </div>
            )}
            {result.details.quality_rating && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Qualidade:</span>
                <Badge variant="outline" className="text-[10px]">
                  {result.details.quality_rating}
                </Badge>
              </div>
            )}
            {result.details.platform_type && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Plataforma:</span>
                <span className="font-medium">{result.details.platform_type}</span>
              </div>
            )}
          </div>
        )}

        {/* Última verificação */}
        {lastCheck && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Clock className="h-3 w-3" />
            {t('inboxDetails.lastCheck', 'Última verificação')}: {' '}
            {formatDistanceToNow(lastCheck, { addSuffix: true, locale: ptBR })}
          </div>
        )}

        {/* Botões de ação */}
        <div className="flex gap-2 pt-2">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleCheckConnection}
            disabled={isChecking || !hasCredentials}
            className="flex-1"
          >
            {isChecking ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            {isChecking 
              ? t('inboxDetails.checking', 'Verificando...') 
              : t('inboxDetails.checkNow', 'Verificar Agora')
            }
          </Button>
          
          {onReconnect && (
            <Button 
              variant="outline" 
              size="sm" 
              onClick={onReconnect}
              className="flex-1"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              {t('inboxDetails.reconnect', 'Reconectar')}
            </Button>
          )}
        </div>

        {/* Mensagem se não tem credenciais */}
        {!hasCredentials && (
          <p className="text-sm text-muted-foreground text-center">
            {t('inboxDetails.configureCredentialsFirst', 'Configure as credenciais primeiro para verificar a conexão.')}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
